import { useCallback, useMemo, useRef, useState } from 'react';
import { DownloadOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Checkbox, ConfigProvider, Flex, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useFlags } from 'launchdarkly-react-client-sdk';
import LoaderEcho from '@/components/LoaderEcho';
import ModalEcho from '@/components/modals/Modal/ModalEcho';
import NoDataMessageEcho from '@/components/states/NoDataMessageEcho';
import DataTableEcho from '@/components/tables/DataTable/DataTableEcho';
import { useDerivedNetworkContext } from '@/context';
import { useColorAssignment } from '@/hooks/useColorAssignment';
import { useDeviceDownloadsQuery } from '@/services/revenueApi';
import type { DateRange, DownloadConfig, ShowFilter, TableData } from '@/types';
import type { DeviceDownloadsResponse } from '@/types/api/devices';
import { checkboxColors } from '@/utils';
import { formatPercentageTwoDp } from '@/utils/numberFormattingUtils';
import {
  trackAudienceTopDevicesModalDeviceSelectionToggled,
  trackAudienceTopDevicesModalDownloadClicked,
  trackAudienceTopDevicesModalOpened,
} from './audienceModalMixpanel';
import TopDevicesModalChartEcho from './TopDevicesModalChartEcho';
import DataAvailabilityBanner from './DataAvailabilityBanner';
import useIsBeforeDataAvailability from './useIsBeforeDataAvailability';
import useDeviceDownloadsCsvExport from './useDeviceDownloadsCsvExport';

const CHECKBOX_COLUMN_WIDTH = 40;

const formatPercentage = (value: number): string => `${formatPercentageTwoDp(value)}%`;

const getCellValue = (record: TableData, columnKey: string) =>
  record.columns.find((column) => column.id === columnKey)?.value ?? null;

const ColoredSelectionCheckbox = ({
  checked,
  color,
  ariaLabel,
  onChange,
}: {
  checked: boolean;
  color?: string;
  ariaLabel: string;
  onChange: () => void;
}) => {
  const checkbox = <Checkbox checked={checked} aria-label={ariaLabel} onChange={onChange} />;

  if (!checked || !color) {
    return checkbox;
  }

  return <ConfigProvider theme={{ token: { colorPrimary: color } }}>{checkbox}</ConfigProvider>;
};

const buildTableData = (devices: DeviceDownloadsResponse['table']['devices']): TableData[] =>
  devices.map((item) => ({
    id: item.device,
    columns: [
      { id: 'device', value: item.device, type: 'string' },
      { id: 'downloads', value: item.downloads.toLocaleString(), type: 'number' },
      { id: 'percentage', value: formatPercentage(item.percentOfTotal), type: 'percentage' },
    ],
  }));

const TopDevicesTable = ({ data }: { data: DeviceDownloadsResponse }) => {
  const { token } = theme.useToken();
  const [selectedIds, setSelectedIds] = useState<string[]>(() =>
    data.table.devices.map((item) => item.device)
  );
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;
  const { getColor } = useColorAssignment(selectedIds);

  const toggle = useCallback((id: string) => {
    const isSelected = selectedIdsRef.current.includes(id);
    trackAudienceTopDevicesModalDeviceSelectionToggled(id, !isSelected);
    setSelectedIds((previousIds) =>
      isSelected ? previousIds.filter((rowId) => rowId !== id) : [...previousIds, id]
    );
  }, []);

  const tableData = useMemo(() => buildTableData(data.table.devices), [data.table.devices]);

  const columns: ColumnsType<TableData> = useMemo(
    () => [
      {
        key: 'checkbox',
        width: CHECKBOX_COLUMN_WIDTH,
        title: '',
        render: (_value, record) => {
          const colorIndex = getColor(record.id);
          const color = colorIndex != null ? checkboxColors[colorIndex] : undefined;
          const isSelected = selectedIds.includes(record.id);

          return (
            <ColoredSelectionCheckbox
              checked={isSelected}
              color={isSelected ? color : undefined}
              ariaLabel={`Select ${record.id}`}
              onChange={() => toggle(record.id)}
            />
          );
        },
      },
      {
        key: 'device',
        title: 'Device',
        sorter: true,
        render: (_value, record) => getCellValue(record, 'device'),
      },
      {
        key: 'downloads',
        title: 'Downloads',
        align: 'right',
        sorter: true,
        render: (_value, record) => getCellValue(record, 'downloads'),
      },
      {
        key: 'percentage',
        title: '% of Total',
        align: 'right',
        sorter: true,
        render: (_value, record) => getCellValue(record, 'percentage'),
      },
    ],
    [getColor, selectedIds, toggle]
  );

  return (
    <Flex vertical gap={token.marginLG} style={{ padding: token.padding }}>
      <TopDevicesModalChartEcho graph={data.graph} selectedIds={selectedIds} getColor={getColor} />
      <DataTableEcho columns={columns} dataSource={tableData} pagination={false} rowKey="id" />
    </Flex>
  );
};

const TopDevicesModalContent = ({
  dateRange,
  selectedShow,
}: {
  dateRange: DateRange;
  selectedShow: ShowFilter;
}) => {
  const { selectedNetwork } = useDerivedNetworkContext();
  const { audienceSources } = useFlags<{ audienceSources?: string[] }>();
  const sources = audienceSources ?? ['acast', 'phyllo'];
  const showIds = selectedShow !== 'all' && selectedShow ? [selectedShow] : undefined;

  const { data, isPending } = useDeviceDownloadsQuery(
    selectedNetwork.id,
    dateRange.from,
    dateRange.to,
    undefined,
    { showIds, sources, granularity: 'day' }
  );

  const isBeforeDataAvailability = useIsBeforeDataAvailability('device', dateRange.from);

  if (isBeforeDataAvailability) {
    return <DataAvailabilityBanner dataType="device" />;
  }

  if (isPending) {
    return (
      <Flex justify="center" align="center" style={{ height: '60vh' }}>
        <LoaderEcho data-testid="loader" />
      </Flex>
    );
  }

  if (!data || data.table.devices.length < 1) {
    return <NoDataMessageEcho dataType="devices data" />;
  }

  return <TopDevicesTable data={data} />;
};

const TopDevicesModalEcho = () => {
  const { handleDeviceDownloadsCSVExport } = useDeviceDownloadsCsvExport();

  return (
    <ModalEcho
      hashProp="devices-details"
      title="Device details"
      showDateRangeFilter
      onOpen={trackAudienceTopDevicesModalOpened}
      downloadConfig={{
        onDownload: (dateRange, selectedShow) => {
          trackAudienceTopDevicesModalDownloadClicked();
          return handleDeviceDownloadsCSVExport(dateRange, selectedShow ?? 'all', 'day');
        },
        downloadButtonText: 'Export as CSV',
        downloadButtonIcon: DownloadOutlined as DownloadConfig['downloadButtonIcon'],
      }}
      renderTrigger={(openModal) => (
        <Flex justify="flex-end" style={{ alignSelf: 'flex-end' }}>
          <Button
            type="text"
            color="primary"
            variant="text"
            icon={<RightOutlined />}
            iconPlacement="end"
            onClick={openModal}
          >
            More details
          </Button>
        </Flex>
      )}
    >
      {(dateRange, selectedShow) => (
        <TopDevicesModalContent dateRange={dateRange} selectedShow={selectedShow} />
      )}
    </ModalEcho>
  );
};

export default TopDevicesModalEcho;
